import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ContextMenuProvider } from "./components/ContextMenu";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { Toaster } from "sonner";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { startGlobalTableEnhancer, rescanTables } from "./utils/tableEnhancer";

// Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import CookieBanner from "./components/CookieBanner";
import Dashboard from "./pages/Dashboard";
import TimeEntryPage from "./pages/TimeEntryPage";
import MyHoursPage from "./pages/MyHoursPage";
import ValidateHoursPage from "./pages/ValidateHoursPage";
import EmployeesPage from "./pages/EmployeesPage";
import ReportsPage from "./pages/ReportsPage";
import HistoryPage from "./pages/HistoryPage";
import SettingsPage from "./pages/SettingsPage";
import PlanningPage from "./pages/PlanningPage";
import MyPlanningPage from "./pages/MyPlanningPage";
import ChatPage from "./pages/ChatPage";
import DocumentsPage from "./pages/DocumentsPage";
import MyDocumentsPage from "./pages/MyDocumentsPage";
import MyEquipmentPage from "./pages/MyEquipmentPage";
import ScanMaterielPage from "./pages/ScanMaterielPage";
import WorksiteFeedHubPage from "./pages/WorksiteFeedHubPage";
import InterfacePreviewsPage from "./pages/InterfacePreviewsPage";
import MyVehiclePage from "./pages/MyVehiclePage";
import ProjectsPage from "./pages/ProjectsPage";
import STPortalPage from "./pages/STPortalPage";
import STPortalDemoPage from "./pages/STPortalDemoPage";
import ClientDemoPage from "./pages/ClientDemoPage";
import ClientReservationsPortalPage from "./pages/ClientReservationsPortalPage";
import ExpensesPage from "./pages/ExpensesPage";
import VATPage from "./pages/VATPage";
import EmployeeExpensesPage from "./pages/EmployeeExpensesPage";
import ShoppingListPage from "./pages/ShoppingListPage";
import InvoicingPage from "./pages/InvoicingPage";
import SiteManagementPage from "./pages/SiteManagementPage";
import ResourcesPage from "./pages/ResourcesPage";
import FinancePage from "./pages/FinancePage";
import AccessManagementPage from "./pages/access/AccessManagementPage";
import InvitationAcceptPage from "./pages/InvitationAcceptPage";
import ProgrammingPage from "./pages/ProgrammingPage";
import OpportunitiesPage from "./pages/OpportunitiesPage";
import SubcontractorsPage from "./pages/SubcontractorsPage";
import NotesTasksPage from "./pages/NotesTasksPage";
import NoteDetailPage from "./pages/NoteDetailPage";
import GmailPage from "./pages/GmailPage";
import BrowserPage from "./pages/BrowserPage";
import CorrespondencePage from "./pages/CorrespondencePage";
import SuppliersPage from "./pages/SuppliersPage";
import ContactsPage from "./pages/ContactsPage";
import MobileHelpPage from "./pages/MobileHelpPage";
import MyProjectsPage from "./pages/MyProjectsPage";
import MaterialsCatalogPage from "./pages/MaterialsCatalogPage";
import MaterialsStockPage from "./pages/MaterialsStockPage";

// Components
import Layout from "./components/Layout";

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-container" style={{ minHeight: '100vh' }}>
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return <Layout><ErrorBoundary>{children}</ErrorBoundary></Layout>;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-container" style={{ minHeight: '100vh' }}>
        <div className="spinner" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  const { user } = useAuth();
  const location = useLocation();

  // Global table enhancer (sort + filter + persist) for all .data-table tables
  useEffect(() => {
    const stop = startGlobalTableEnhancer();
    return () => { try { stop(); } catch { /* noop */ } };
  }, []);
  // Re-scan after every route change (let new content render first)
  useEffect(() => {
    const t = setTimeout(() => rescanTables(), 200);
    return () => clearTimeout(t);
  }, [location.pathname]);

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/confidentialite" element={<PrivacyPolicyPage />} />
      <Route path="/invitation/:token" element={<InvitationAcceptPage />} />
      <Route path="/st/offer/:token" element={<STPortalPage />} />
      <Route path="/st/demo" element={<STPortalDemoPage />} />
      <Route path="/client/demo" element={<ClientDemoPage />} />
      <Route path="/client/reserves/:token" element={<ClientReservationsPortalPage />} />

      {/* Protected Routes - Both Employee and Admin */}
      <Route path="/" element={
        <ProtectedRoute>
          {user?.role === 'employee' ? <Dashboard /> : <Dashboard />}
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />

      {/* Employee Only Routes */}
      <Route path="/time-entry" element={<ProtectedRoute><TimeEntryPage /></ProtectedRoute>} />
      <Route path="/my-hours" element={<ProtectedRoute><MyHoursPage /></ProtectedRoute>} />
      <Route path="/my-planning" element={<ProtectedRoute><MyPlanningPage /></ProtectedRoute>} />

      {/* Both Employee and Admin Routes */}
      <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/contacts" element={<ProtectedRoute><ContactsPage /></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />
      <Route path="/my-documents" element={<ProtectedRoute><MyDocumentsPage /></ProtectedRoute>} />
      <Route path="/my-equipment" element={<ProtectedRoute><MyEquipmentPage /></ProtectedRoute>} />
      <Route path="/scan-materiel" element={<ProtectedRoute><ScanMaterielPage /></ProtectedRoute>} />
      <Route path="/apercus-interfaces" element={<ProtectedRoute><InterfacePreviewsPage /></ProtectedRoute>} />
      <Route path="/my-vehicle" element={<ProtectedRoute><MyVehiclePage /></ProtectedRoute>} />
      <Route path="/finance" element={<ProtectedRoute><FinancePage /></ProtectedRoute>} />
      <Route path="/employee-expenses" element={<ProtectedRoute><FinancePage /></ProtectedRoute>} />
      <Route path="/projects" element={<ProtectedRoute><SiteManagementPage /></ProtectedRoute>} />
      <Route path="/invoicing" element={<ProtectedRoute><SiteManagementPage /></ProtectedRoute>} />
      <Route path="/shopping-list" element={<ProtectedRoute><SiteManagementPage /></ProtectedRoute>} />

      {/* Both Employee and Admin - Gestion des Salaries (employees see only Conges/Arrets tabs) */}
      <Route path="/employees" element={<ProtectedRoute><EmployeesPage /></ProtectedRoute>} />

      {/* Worksite feed hub (chantier picker + feed) — available to both admin & employee */}
      <Route path="/fil-actualite" element={<ProtectedRoute><WorksiteFeedHubPage /></ProtectedRoute>} />

      {/* Admin Only Routes */}
      <Route path="/validate" element={<ProtectedRoute adminOnly><ValidateHoursPage /></ProtectedRoute>} />
      <Route path="/planning" element={<ProtectedRoute adminOnly><PlanningPage /></ProtectedRoute>} />
      <Route path="/expenses" element={<ProtectedRoute adminOnly><FinancePage /></ProtectedRoute>} />
      <Route path="/vat" element={<ProtectedRoute adminOnly><FinancePage /></ProtectedRoute>} />
      <Route path="/resources" element={<ProtectedRoute adminOnly><ResourcesPage /></ProtectedRoute>} />
      <Route path="/history" element={<ProtectedRoute adminOnly><HistoryPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute adminOnly><SettingsPage /></ProtectedRoute>} />
      <Route path="/access" element={<ProtectedRoute adminOnly><AccessManagementPage /></ProtectedRoute>} />
      <Route path="/pending-registrations" element={<Navigate to="/access" replace />} />
      <Route path="/programmation" element={<ProtectedRoute adminOnly><ProgrammingPage /></ProtectedRoute>} />
      <Route path="/opportunites" element={<ProtectedRoute adminOnly><OpportunitiesPage /></ProtectedRoute>} />
      <Route path="/sous-traitance" element={<ProtectedRoute adminOnly><SubcontractorsPage /></ProtectedRoute>} />
      <Route path="/notes-taches" element={<ProtectedRoute><NotesTasksPage /></ProtectedRoute>} />
      <Route path="/notes-taches/:noteId" element={<ProtectedRoute><NoteDetailPage /></ProtectedRoute>} />
      <Route path="/gmail" element={<ProtectedRoute adminOnly><GmailPage /></ProtectedRoute>} />
      <Route path="/correspondance" element={<ProtectedRoute adminOnly><CorrespondencePage /></ProtectedRoute>} />
      <Route path="/fournisseurs" element={<ProtectedRoute adminOnly><SuppliersPage /></ProtectedRoute>} />
      <Route path="/aide-mobile" element={<ProtectedRoute><MobileHelpPage /></ProtectedRoute>} />
      <Route path="/mes-chantiers" element={<ProtectedRoute><MyProjectsPage /></ProtectedRoute>} />
      <Route path="/materiaux" element={<ProtectedRoute adminOnly><MaterialsCatalogPage /></ProtectedRoute>} />
      <Route path="/stocks-materiaux" element={<ProtectedRoute><MaterialsStockPage /></ProtectedRoute>} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LanguageProvider>
        <Toaster 
          position="top-right" 
          richColors 
          toastOptions={{
            style: {
              fontFamily: "'IBM Plex Sans', sans-serif",
            }
          }}
        />
        <AppRoutes />
        <CookieBanner />
        <ContextMenuProvider><span hidden /></ContextMenuProvider>
        </LanguageProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
